import React, { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import type { User } from '../types';
import { authApi } from '../api/client';
import { initAdminNotifications } from '../services/notifications';

interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<User>;
  register: (email: string, password: string) => Promise<User>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let storedToken: string | null = null;
    let storedUser: string | null = null;
    try {
      storedToken = localStorage.getItem('auth_token');
      storedUser = localStorage.getItem('user');
    } catch {
      // storage unavailable — behave as logged out
    }

    if (storedToken && storedUser) {
      try {
        setToken(storedToken);
        setUser(JSON.parse(storedUser));
        initAdminNotifications();
      } catch {
        try {
          localStorage.removeItem('auth_token');
          localStorage.removeItem('user');
        } catch {
          // ignore storage errors
        }
      }
    }
    setIsLoading(false);
  }, []);

  const login = async (email: string, password: string) => {
    const response = await authApi.login(email, password);
    const raw = response.data.data.token;
    const newToken = typeof raw === 'string' ? raw : (raw as any)?.token || (raw as any)?.user?.token || '';
    if (!newToken || typeof newToken !== 'string') {
      throw new Error('Invalid token received from server');
    }
    const authUser: User = {
      _id: '',
      email,
      role: (response.data.data as any)?.role,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    localStorage.setItem('auth_token', newToken);
    localStorage.setItem('user', JSON.stringify(authUser));
    setToken(newToken);
    setUser(authUser);
    initAdminNotifications();
    return authUser;
  };

  const register = async (email: string, password: string) => {
    const response = await authApi.register(email, password);
    const raw = response.data.data.token;
    const newToken = typeof raw === 'string' ? raw : (raw as any)?.token || (raw as any)?.user?.token || '';
    const authUser: User = {
      _id: (response.data.data as any)?.user?.id || '',
      email,
      role: (response.data.data as any)?.user?.role,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    if (newToken && typeof newToken === 'string') {
      localStorage.setItem('auth_token', newToken);
      setToken(newToken);
    }
    localStorage.setItem('user', JSON.stringify(authUser));
    setUser(authUser);
    return authUser;
  };

  const logout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isLoading,
        isAuthenticated: !!token,
        login,
        register,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}